import { __assign, __awaiter, __generator, __spreadArray } from "tslib";
import { isObject, isPlainObject, omit, pick, get, set } from 'lodash';
import { useRouter, useRoute } from 'vue-router';
import { onMounted, unref, isRef } from 'vue';
import { isArray, isEmpty, getUrlParams } from './utils';
import { arrayToPah, flattenObj } from './stringify';
import { DECODE_MAP } from './parse';
var DEFAULT_CONFIG = {
    pickKey: [],
    omitKey: [],
    type: {},
};
/**
 * syncAfterDataChange
 * type: { a: 'array' }
 * data 为对象，且config配置多余的key，会进行同步
 * data 不能为ref值，ref值无key
 */
export default function useSyncRouter(data, config) {
    var _a;
    // 配置类型
    var _b = __assign(__assign({}, DEFAULT_CONFIG), config), type = _b.type, afterDataUpdate = _b.afterDataUpdate, omitKey = _b.omitKey, pickKey = _b.pickKey;
    var router = useRouter();
    var route = useRoute();
    // 是否调用 afterDataUpdate 方法
    var callAfterData = false;
    var debug = ((_a = route === null || route === void 0 ? void 0 : route.query) === null || _a === void 0 ? void 0 : _a.debug) || false;
    onMounted(function () {
        syncFromRouter();
    });
    function syncObject(obj, query) {
        if (query === void 0) { query = {}; }
        var item = unref(obj);
        if ((pickKey === null || pickKey === void 0 ? void 0 : pickKey.length) > 0) {
            item = pick(item, pickKey || []);
        }
        // 拿到 config.type 配置的key与 传入obj的key
        var typeKeys = Object.keys(type);
        var itemKeys = Object.keys(item);
        var keys = Array.from(new Set(__spreadArray(__spreadArray([], typeKeys, true), itemKeys, true)));
        for (var i = 0; i < keys.length; i++) {
            var key = keys[i];
            if (omitKey && (omitKey === null || omitKey === void 0 ? void 0 : omitKey.includes(key))) {
                continue;
            }
            if (query[key]) {
                // 是否针对某个key有类型配置
                var typeKey = type === null || type === void 0 ? void 0 : type[key];
                // 传入数据每个key对应的初始值，可能传入数据是{ a: ref }
                var value = unref(item[key]);
                // 判断数据值是否为ref
                var refItem = isRef(item[key]);
                // 初始值类型 ,todo 解析对象
                var valueType = Array.isArray(value) ? 'array' : typeof value;
                // 如果配置了类型，则使用，否则判断传入数据对应key的类型
                var decodeFunc = DECODE_MAP[typeKey || valueType];
                if (decodeFunc) {
                    var decodeValue = decodeFunc("".concat(query[key]));
                    if (isRef(obj)) {
                        set(obj.value, key, decodeValue);
                    }
                    else if (refItem) {
                        set(get(obj, key), 'value', decodeValue);
                    }
                    else {
                        set(obj, key, decodeValue);
                    }
                }
            }
        }
    }
    function syncFromRouter() {
        var _a;
        return __awaiter(this, void 0, void 0, function () {
            var query, queryName, queryData, parseStr;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        query = (route === null || route === void 0 ? void 0 : route.query) || getUrlParams();
                        queryName = (config === null || config === void 0 ? void 0 : config.name) || '';
                        queryData = query;
                        try {
                            if (query === null || query === void 0 ? void 0 : query[queryName]) {
                                parseStr = debug ? query[queryName] : decodeURIComponent(query[queryName]);
                                queryData = JSON.parse(parseStr);
                            }
                            // eslint-disable-next-line no-empty
                        }
                        catch (e) { }
                        if (Array.isArray(data)) {
                            (_a = ((data || []))) === null || _a === void 0 ? void 0 : _a.forEach(function (item) {
                                syncObject(item, queryData);
                            });
                        }
                        else if (isObject(data)) {
                            syncObject(data, queryData);
                        }
                        if (!afterDataUpdate) return [3 /*break*/, 2];
                        callAfterData = true;
                        return [4 /*yield*/, afterDataUpdate()];
                    case 1:
                        _b.sent();
                        callAfterData = false;
                        _b.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    }
    /**
     * 生成router参数
     * @param data
     * @param config
     */
    function generateRouterQuery(data, config) {
        var _a;
        if (data === void 0) { data = {}; }
        var query = (route === null || route === void 0 ? void 0 : route.query) || getUrlParams();
        var result = getValidData(data, config);
        var routerQuery = {};
        var flattenData = {};
        flattenObj(result, flattenData);
        Object.keys(flattenData).forEach(function (key) {
            // 对value进行编码
            var value = flattenData[key];
            // 避免路由出现一堆无效key
            // 1、只同步路由有效值，或2、路由有值且data[key]为空值
            if (!isEmpty(value) || (isEmpty(value) && query[key])) {
                if (Array.isArray(value)) {
                    routerQuery[key] = arrayToPah(value);
                }
                else {
                    routerQuery[key] = value;
                }
            }
        });
        try {
            if (config === null || config === void 0 ? void 0 : config.name) {
                return _a = {}, _a[config.name] = debug ? JSON.stringify(routerQuery) : encodeURIComponent(JSON.stringify(routerQuery)), _a;
            }
            // eslint-disable-next-line no-empty
        }
        catch (e) { }
        return routerQuery;
    }
    function syncToRouter(addData) {
        if (addData === void 0) { addData = {}; }
        if (callAfterData) {
            return;
        }
        var routerQuery = generateRouterQuery(addData, config);
        var originQuery = generateRouterQuery(data, config);
        var query = (route === null || route === void 0 ? void 0 : route.query) || getUrlParams();
        router.push({
            path: route.path,
            query: __assign(__assign(__assign({}, query), originQuery), routerQuery),
        });
    }
    return {
        syncToRouter: syncToRouter,
        syncFromRouter: syncFromRouter,
    };
}
/**
 * 判断传入的需要同步router的data数据是否符合要求
 * @param data
 * @param config
 */
function getValidData(data, config) {
    var _a;
    var result = {};
    var temp = [];
    var unrefData = unref(data);
    if (isPlainObject(unrefData)) {
        temp = [unrefData];
    }
    else if (isArray(data)) {
        temp = data;
    }
    else {
        console.error(TypeError('[sync-router Error]：传入的的data参数，只能为数组或对象'));
    }
    for (var i = 0; i < temp.length; i++) {
        var unrefItem = unref(temp[i]);
        if (isPlainObject(unrefItem)) {
            // pick优先级高
            Object.assign(result, unrefItem);
        }
        else {
            console.error("\u4F20\u5165data[".concat(i, "] = ").concat(data[i], " \u4E3A\u975E\u5BF9\u8C61\uFF0C\u5C06\u4E0D\u88AB\u5904\u7406"));
        }
    }
    var omitItem = omit(result, (config === null || config === void 0 ? void 0 : config.omitKey) || []);
    if ((_a = config === null || config === void 0 ? void 0 : config.pickKey) === null || _a === void 0 ? void 0 : _a.length) {
        return pick(omitItem, (config === null || config === void 0 ? void 0 : config.pickKey) || []);
    }
    return omitItem;
}
